import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {TopicDto} from "@/store/forum/types";
import {ForumUtils} from "@/utils/forum-utils";
import {Link} from "@/scenes.ui/link";
import {Tr} from "@/i18n/i18n";
import PlayerStore from "@/store/player";
import BBCodeText from "phaser3-rex-plugins/plugins/bbcodetext";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "../utils/c-u";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {IntegrationUtils} from "@/utils/integration-utils";

export class TopicPanel extends Phaser.GameObjects.Container {

    private myVote!: number;
    private rate!: number;

    public static CUT_SYMBOLS_LIMIT = 900;

    private profileCallback!: (playerId:number) => unknown;
    private topicCallback!: ((topicId:number) => unknown) | undefined;

    constructor(scene: Phaser.Scene, t: TopicDto, rateCallback: (rate: number) => unknown,
                topicCallback: ((topicId:number) => unknown) | undefined, highlighted: boolean, settVisible: boolean,
                settingCallback: () => unknown, profileCallback: (playerId:number) => unknown, cut?: boolean) {
        super(scene);

        this.scene = scene;
        this.width = CU.CANVAS_WIDTH;
        this.rate = t.rate;
        this.myVote = t.myVote;
        this.profileCallback = profileCallback;
        this.topicCallback = topicCallback;

        let self = this;

        const titleLight = SpriteUtils.addImage(this.scene, 20, 3, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_LIGHT)
            .setOrigin(0, 0);
        titleLight.alpha = 0.7;
        SpriteUtils.setDispSize(titleLight, 360 / 16 * t.title.length * CU.S, 65 * CU.S);

        let titleText = TextUtils.addTextAdvanced(scene, t.title, 70, 40,
            '#ffffff', 18, 'normal', 500, 'left', 2, Fonts.BENGALY_NORMAL);

        let authorText = TextUtils.addTextAdvanced(scene, Tr('auth') + t.playerName, 70, 60,
            '#ffd565', 13, 'normal', 500, 'left', 1, Fonts.MYRIADPRO_REGULAR);
        authorText.setOrigin(0, 0);

        authorText.setInteractive().on('pointerup', () => {
            profileCallback(t.playerId);
        });

        let tagsText = TextUtils.addTextAdvanced(scene, ForumUtils.tagsToString(t.tags), authorText.getRightCenter().x! / CU.S + 15, 62,
            '#ffe49e', 11, 'normal', 500, 'left', 1, Fonts.MYRIADPRO_REGULAR);
        tagsText.setOrigin(0, 0);

        let guestText = TextUtils.addTextAdvanced(scene, t.guest ? Tr('guest') : '', tagsText.getRightCenter().x! / CU.S + 10, 62,
            '#98d2ff', 11, 'normal', 500, 'left', 1, Fonts.MYRIADPRO_REGULAR);
        guestText.setOrigin(0, 0);

        let isCut = t.text.length > TopicPanel.CUT_SYMBOLS_LIMIT && cut;
        let txt = !isCut ? t.text : (t.text.substr(0, TopicPanel.CUT_SYMBOLS_LIMIT) + '.....');

        let texts:BBCodeText[] = [];
        let links:Link[] = [];

        let y = 90 * CU.S;
        try{
            let parts = txt.split(new RegExp('\\{\\{|\\}\\}'));
            let lastBlockWasLink = false;
            parts.forEach((part, i) => {
                if(part.includes('||') ){
                    let linkParts = part.split('||');
                    let referencedTopicId = linkParts[0];
                    let referencedTopicName = linkParts[1];
                    let link = this.addLink(scene, referencedTopicId, referencedTopicName, y);
                    links.push(link);
                    y += link.height  * CU.S + 28 * CU.S //+ (lastBlockWasLink ? 0 : 10 * CU.S);
                    lastBlockWasLink = true;
                } else if (part.trim() != '') {
                    let mainText = this.addText(scene, t, part, y);
                    texts.push(mainText);
                    y += mainText.height  * CU.S + 3 * CU.S // + 10 * CU.S;
                    lastBlockWasLink = false;
                }
            });
        } catch(e){
            let mainText = this.addText(scene, t, txt, y);
            texts.push(mainText);
            y += mainText.height  * CU.S + 6 * CU.S;
        }

        let tmTxt = Tr('crtd') + t.createdAt;
        if (t.editedAt && t.editorName ) {
            tmTxt += (Tr('edtd') + t.editorName + ' ' + t.editedAt);
        }

        this.height = 170 + y/CU.S - 90;

        let link: Link | undefined;
        if (topicCallback && !highlighted) {
            let toTopText = isCut ?  Tr('read.all') :  Tr('to.top') ;
            link = new Link(scene, 70 * CU.S, this.height * CU.S - 40 * CU.S , toTopText, () => {
                topicCallback(t.id);
            }, isCut? '#b3dea3' : '#D0D0D0');
        }

        let timesText = TextUtils.addTextAdvanced(scene, tmTxt, 70, this.height - 68,
            '#a1a1a1', 12, 'normal', 500, 'left', 1, Fonts.MYRIADPRO_REGULAR);
        timesText.setOrigin(0, 0);

        const back = SpriteUtils.addImage(this.scene, 0, 0, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_PANEL_BACK).setOrigin(0, 0);
        SpriteUtils.setDispSize(back, CU.CANVAS_WIDTH, this.height*CU.S);

        const highlightedBack = SpriteUtils.addImage(this.scene, 0, -4, Keys.ATLAS_PROFILE, Keys.UI_SCENARIO_TASK_BACK).setOrigin(0, 0)//.setVisible(highlighted);
        highlightedBack.alpha = 0.6;
        SpriteUtils.setDispSize(highlightedBack, CU.CANVAS_WIDTH, this.height*CU.S);

        let rateText = TextUtils.addTextAdvanced(scene, String(t.rate), 35, 66,
            '#eaeaea', 18, 'normal', 500, 'left', 1, Fonts.MYRIADPRO_REGULAR);
        rateText.setOrigin(0.5, 0.5);

        let upIcon = SpriteUtils.addImage(this.scene!, 35, 22, t.myVote > 0 ? Keys.FRM_UP_GREEN : Keys.FRM_UP)
            .setOrigin(0.5, 0.0);

        let downIcon = SpriteUtils.addImage(this.scene!, 35, 107, t.myVote < 0 ? Keys.FRM_UP_GREEN : Keys.FRM_UP, undefined, 1.0, -1.0)
            .setOrigin(0.5, 0.0);

        upIcon!.setInteractive().on('pointerdown', () => {
            if (self.myVote > 0) {
                self.rate = self.rate - 1;
            } else if (self.myVote < 0) {
                self.rate = self.rate + 2;
            } else {
                self.rate = self.rate + 1;
            }
            self.myVote = self.myVote > 0 ? 0 : 1;
            rateCallback(self.myVote);
            SpriteUtils.setSprite(scene, upIcon, self.myVote > 0 ? Keys.FRM_UP_GREEN : Keys.FRM_UP);
            SpriteUtils.setSprite(scene, downIcon, self.myVote < 0 ? Keys.FRM_UP_GREEN : Keys.FRM_UP);
            rateText.setText(String(self.rate));
        });

        downIcon!.setInteractive().on('pointerdown', () => {
            if (self.myVote < 0) {
                self.rate = self.rate + 1;
            } else if (self.myVote > 0) {
                self.rate = self.rate - 2;
            } else {
                self.rate = self.rate - 1;
            }
            self.myVote = self.myVote < 0 ? 0 : -1;
            rateCallback(self.myVote);
            SpriteUtils.setSprite(scene, upIcon, self.myVote > 0 ? Keys.FRM_UP_GREEN : Keys.FRM_UP);
            SpriteUtils.setSprite(scene, downIcon, self.myVote < 0 ? Keys.FRM_UP_GREEN : Keys.FRM_UP);
            rateText.setText(String(self.rate));
        });

        let player = PlayerStore.getPlayer();
        if (player.level < 10) {
            downIcon.setVisible(false);
            upIcon.setVisible(false);
        }

        let pinnedIcon = SpriteUtils.addImage(this.scene!, 555, settVisible? 80 : 30, Keys.FRM_PIN_ROUND, undefined, 0.9).setOrigin(0.5, 0.5).setAlpha(0.9);
        pinnedIcon.setVisible(t.pinned);

        let copyIcon = SpriteUtils.addImage(this.scene!, 555, (settVisible? 80 : 30) + (t.pinned ? 50 : 0) , Keys.FRM_COPY_ROUND, undefined, 0.9).setOrigin(0.5, 0.5).setAlpha(0.9);
        copyIcon.setInteractive().on('pointerup', () => {
            new FeedbackPanel(self.scene, Tr('message.copied'), undefined, true).show();
            let text = TextUtils.removeBracketsAndCurlyBraces(t.text);
            IntegrationUtils.copyToClipboard(text);
        });
        copyIcon.setVisible(cut? false : IntegrationUtils.isClipboardAPIEnabled());

        const separator = SpriteUtils.addSprite(this.scene!, 0, this.height, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR).setOrigin(0, 1);

        this.add([back, highlightedBack, authorText, tagsText, guestText, titleLight,
            titleText, ...texts, timesText, rateText, downIcon, upIcon, pinnedIcon, copyIcon, ...links, separator]);
        if (link) {
            this.add(link);
        }

        if (settVisible) {
            let settButton = SpriteUtils.addImage(this.scene!, 555, 30, Keys.FRM_RADIO_OUT, undefined, 0.9).setOrigin(0.5, 0.5).setAlpha(0.9);
            let settText = this.scene!.add.text(555 * CU.S, 28 * CU.S, '...').setOrigin(0.5, 0.5).setScale(CU.S, CU.S);

            settButton.setInteractive().on('pointerdown', () => {
                settingCallback();
            });
            this.add([settButton, settText]);
        }
    }

    private addText(scene:Phaser.Scene, t:TopicDto, txt:string, y:number):BBCodeText{
        txt = txt.replace(new RegExp('\\[img', 'g'), '[' + Tr('pict.not.sup') + ']');
        txt = txt.replace(new RegExp('\\[url', 'g'), '[' + Tr('pict.not.sup') + ']');
        let mainText = new BBCodeText(scene, 70 * CU.S, y, txt, { //90 * CU.S + offsetY, txt, {
            fontFamily: Fonts.MYRIADPRO_REGULAR,
            fontSize: '13pt',
            color: t.admin? '#d6b1ff': t.moder?  '#ffefef' : '#ffffff',
            shadow: {
                offsetX: -1,
                offsetY: 2,
                color: '#000000'
            },
            stroke: '#000000',
            align: 'left',
            wrap: {
                mode: 1,
                width: 500
            }
        }).setScale(CU.S, CU.S);
        return mainText;
    }

    private addLink(scene:Phaser.Scene, referencedTopicId:string, referencedTopicName:string, y:number):Link{
        let topicLink = new Link(scene, 70 * CU.S,  y, referencedTopicName, () => {//mainText.getBottomCenter().y  + offsetY * CU.S
            if(referencedTopicName && this.topicCallback) {
                if(referencedTopicId.startsWith('player')) {
                    this.profileCallback(Number(referencedTopicId.replace('player', '')));
                } else {
                    this.topicCallback(Number(referencedTopicId));
                }
            }
        }, '#ffffdd');
        if(!referencedTopicName || !this.topicCallback) {
            topicLink.setVisible(false);
        }
        return topicLink;
    }


}
